/**
 * Migrated carry-over exemptions for policy endorsements (#37040).
 *
 * A policy migrated from a legacy system (CFM: Guidewire GO → EmberTech) arrives with fields the
 * legacy system never collected (an ATV's make and model, a grain leg's height, a dwelling's structure
 * category) and with values EmberTech's newer rules reject (a 100 sq ft shed, an ACV Class 2 barn).
 * The endorsement screen then lists them under "Review and fix these issues", and the agent or UW
 * can't continue without inventing data. Mutual Services' rule: an item that came over in the
 * migration may keep those fields as they arrived, on the imported term, at renewal and on every
 * later endorsement. Everything else keeps full validation.
 *
 * How:
 *  1. `hydrateMigratedCarryOverForForm` runs on the Details form's initial values. On a policy whose
 *     lineage is migrated (`isMigratedPolicyLineage`), it stamps each existing item (risk, pool, scheduled
 *     item, liability asset or property, interested party, car, trailer) with a form-only `migratedCarryOver`
 *     marker: the value each allowlisted field arrived with, plus the item's identity fields (risk
 *     type, structure category, item category, ...).
 *  2. `filterMigratedCarryOverErrors` drops a validation error when its field is allowlisted for the
 *     nearest marked item, still holds the value it arrived with, and the item's identity fields are
 *     unchanged.
 *
 * So the exemption is per item and per field:
 *  - an item added during the endorsement has no marker, so it is fully validated;
 *  - an item added after the migration was fully validated when it was added, so it arrives complete;
 *  - a field the user edits (or clears) is validated again;
 *  - an item turned into something else (another item category, structure category or occupancy) is
 *    validated again, because what arrived described a different item.
 *
 * One coverage limit is allowlisted, by product decision: Coverage C below the percent-of-Coverage-A
 * minimum. On the imported term itself (#37093) any such Coverage C: GO stored whole-dollar Coverage C,
 * so `A × 75%` with cents put it just under the rule. On a migrated renewal (#37109) only a genuinely
 * lower ratio (short by $1 or more, e.g. a deliberate 50%-of-A Coverage C), which the renewal keeps;
 * a sub-dollar shortfall is raised to the minimum at renewal instead (farm-post-data-rules). Either way
 * only while Coverage C and its rule inputs are unchanged.
 *
 * Details-tab validation uses this (customer/components/policy/details), and so does the FO-6 form's
 * roof settlement rule (`holdsMigratedCarryOverValue`, #37110): a roof settlement GO wrote that
 * EmberTech's rules don't offer stays as it arrived until the user changes it or what it resolves from.
 * The farm post-data rules keep it server-side on the same terms. The add-item forms are unchanged.
 * The marker is never persisted: the sanitizer only copies fields declared on the GraphQL input types
 * (core/helpers/sanitize.js).
 *
 * Brand opt-in: `relaxMigratedCarryOverValidation` (CFM) turns on every carry-over below.
 * `relaxMigratedRequiredFields` (Forreston, #37233) turns on every field exemption except the coverage-limit
 * ones (Coverage C below the minimum, locked Coverage D), which mirror CFM's post-data rules, and restores load-time
 * defaults only on Scheduled farm property items (#37243); a Coverage C at or above the unrounded minimum is kept
 * (#37244). Before it, `keepMigratedRoofSettlement` (Forreston, #37115) turned on only the FO-6 roof settlement.
 *
 * On Forreston's scope, a risk whose 360Value is re-run during the endorsement (a UW turns on "Refresh 360Value", or
 * its Structure Category changes) loses its field exemptions, as it is valued like new (#37233).
 */
import { isEqual, toPath } from 'lodash-es';
import { isOriginalImportedTerm } from '@ourbranch/policy-utils';
import { isGenuinelyBelowCoverageCMinimum, resolveFarmCoverageCRules } from '@ourbranch/farm-utils';
import { FarmRiskType } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';
import { isImportedPolicy } from 'core/helpers/policy-status';
import { wasRenewedFromImportedLineage } from 'common/components/farm/uw-questions-form/helpers';
import { getWindHailOptions } from 'common/components/farm/locations/risks/risk-types/FO6-scheduled/wind-hail-deductible-options';

export const MIGRATED_CARRY_OVER_MARKER = 'migratedCarryOver';

/**
 * The fields each kind of item may keep as it arrived, and the identity fields that must stay the
 * same for that to apply. Paths are relative to the item. Only data the legacy system didn't carry,
 * or carried in a shape EmberTech now rejects, belongs here. Coverage limits and selections don't.
 *
 * A field that is only required because some coverage or selection is on (a well pump's value once
 * a pump is scheduled, an exclusion's description once the exclusion is on) names that selection in
 * `controllers`. It keeps its arrived value only while every controller still holds the value it
 * arrived with, so electing the coverage during the endorsement is validated like any new choice.
 * `activeWhen` additionally limits the exemption to items whose coverage was already on when they
 * arrived; it is also handed the term (`originalImportedTerm`) and the item's location `state`.
 * Controllers that are also identity fields (risk type, structure category, item category, liability
 * type) are covered by `identity` and not repeated.
 */
const COSMETIC_SURFACING_FLAGS = [
  'coverages.exteriorDoorsWindowsDamageExclusion',
  'coverages.exteriorWallsDamageExclusion',
  'coverages.roofsDamageExclusion'
];
const arrivedWithoutCosmeticSurfacingFlags = (risk) =>
  risk?.type === FarmRiskType.Fo6Structures && COSMETIC_SURFACING_FLAGS.every((path) => getAt(risk, path) == null);

/** Forreston's wind/hail floor: its farm post-data rules accept any dollar deductible of at least this. */
export const FORRESTON_WIND_HAIL_FLOOR = 1500;

/**
 * Whether a migrated Forreston risk's wind/hail deductible may stay as it arrived, a dollar amount (not a percentage)
 * that what validates it accepts:
 *  - an FO-1/2/3 dwelling (#37237): at least the $1,500 floor and at least All Peril (post-data rules and schema);
 *  - an FO-6 Unscheduled risk (#37242): at least All Peril (the schema; the post-data rules have no rule for it).
 * Any other risk: no (an FO-4's options derive from Coverage C).
 */
const keepsArrivedWindHailDeductible = (risk) => {
  const windHail = Number(risk?.coverages?.deductibleWindHail);
  const allPeril = Number(risk?.coverages?.deductibleAllOther) || 0;
  if (MIGRATED_DWELLING_TYPES.includes(risk?.type)) {
    return windHail >= Math.max(FORRESTON_WIND_HAIL_FLOOR, allPeril);
  }
  return risk?.type === FarmRiskType.Fo6Unscheduled && windHail >= Math.max(1, allPeril);
};

const CARRY_OVER_RULES = {
  // farmLocations[].risks[]
  risk: {
    fields: {
      'building.propertyType': {}, // "Structure category is required" (FO-3 / FO-4); controller: risk type
      'building.height': {}, // silos & grain bins (GO has no height); controllers: structure category, occupancy
      'building.sqFt': { controllers: ['building.propertyType'] }, // barndominium / mobile-home rules
      'building.roofShape': { controllers: ['building.propertyType'] }, // FO-3 barndominium rule
      'building.residenceUsage': {}, // controller: risk type
      // #37141: GO carries no ownership status. The dwelling forms fill in "P" when their card mounts, but a dwelling on
      // a location the user never opens is validated as it arrived ("Ownership status is required"). Controller: risk type.
      'building.ownershipStatus': {},
      'building.primaryHeatingSource': { controllers: ['building.propertyType'] },
      // GO carries a pump count only. Required once pumps are scheduled (or the other field is filled).
      'building.wellPumpEstimatedValue': {
        controllers: ['building.numWellPumps', 'building.wellPumpDescription'],
        activeWhen: (risk) => getAt(risk, 'building.numWellPumps') > 0
      },
      'building.wellPumpDescription': {
        controllers: ['building.numWellPumps', 'building.wellPumpEstimatedValue'],
        activeWhen: (risk) => getAt(risk, 'building.numWellPumps') > 0
      },
      // GO settled some barn classes differently; the rules key on category, class and (confinement /
      // poultry) building age.
      'coverages.structureLossSettlement': { controllers: ['building.yearBuilt'] },
      'coverages.roofLossSettlement': {
        controllers: ['coverages.structureLossSettlement', 'coverages.roofExclusionRestriction', 'building.yearBuilt']
      },
      'coverages.exteriorWallSurfacingExclusionDetails': {
        controllers: ['coverages.exteriorWallSurfacingExclusion'],
        activeWhen: (risk) => Boolean(getAt(risk, 'coverages.exteriorWallSurfacingExclusion'))
      },
      connectedHome: {}, // absent on migrated dwellings; controller: risk type
      // #37133: GO's FO-6 occupancy ("Other") and roof type. The FO-6 form rewrote them on load (poultry →
      // "PCH", hoop → "NONEH"), and occupancy is a 360Value input, so an untouched imported structure was
      // re-valued on every endorsement. Kept until the structure category changes (identity).
      // #37141: GO wrote an FO-6 structure's cosmetic damage exclusion but not its three surfacing flags. The
      // FO-6 form fills them in from the exclusion when its card mounts, and restoreMigratedLoadDefaults sends
      // them back blank; but a structure on a location the user never opens is validated as it arrived, so
      // adding a location blocked on "at least one cosmetic damage surfacing type". Each flag, and the
      // exclusion's own at-least-one rule, keeps the blank while the exclusion and the other flags are as
      // they arrived.
      ...Object.fromEntries(
        COSMETIC_SURFACING_FLAGS.map((flag) => [
          flag,
          {
            controllers: ['coverages.cosmeticDamageExclusion', 'coverages.cosmeticDamageUwOverride'],
            activeWhen: arrivedWithoutCosmeticSurfacingFlags
          }
        ])
      ),
      'coverages.cosmeticDamageExclusion': {
        controllers: [...COSMETIC_SURFACING_FLAGS, 'coverages.cosmeticDamageUwOverride'],
        activeWhen: arrivedWithoutCosmeticSurfacingFlags
      },
      // #37237 (Forreston): IMT carried flat-dollar wind/hail deductibles ($2,500, $1,500) that aren't among today's
      // options, and the Forreston field rewrote them to 1% on load. Kept while Coverage A and All Peril, which the
      // options derive from, are as they arrived. #37242: likewise an FO-6 Unscheduled risk's, which the field forced
      // to max($5,000, All Peril). Forreston's only: CFM's GO deductibles have their own grandfathering (#36996).
      // Only a deductible Forreston keeps (keepsArrivedWindHailDeductible): the Details-tab error filter reads this too,
      // for dwellings whose field never mounts, so one under All Peril must stay an error (adversarial review, #37238).
      'coverages.deductibleWindHail': {
        controllers: ['coverages.coverageA', 'coverages.deductibleAllOther'],
        activeWhen: (risk) =>
          Boolean(appConfig?.featureFlags?.relaxMigratedRequiredFields) && keepsArrivedWindHailDeductible(risk)
      },
      'building.occupancy': { activeWhen: (risk) => risk?.type === FarmRiskType.Fo6Structures },
      'building.roofType': { activeWhen: (risk) => risk?.type === FarmRiskType.Fo6Structures },
      // #37093: GO's Coverage C, a few cents under the unrounded percent-of-A minimum, stays on the imported
      // term while Coverage A and the rule's inputs are unchanged. #37109: a migrated renewal keeps only a
      // genuinely lower ratio (a sub-dollar shortfall is raised at renewal), so only that is exempt there.
      // Primary / Seasonal only: those are the percent-of-A rules; Rental / Vacant are flat-dollar bands
      // the backend enforces as written (adversarial review on #37094).
      'coverages.coverageC': {
        controllers: ['coverages.coverageA', 'coverages.lossSettlement', 'building.residenceUsage'],
        activeWhen: (risk, { originalImportedTerm, state }) =>
          ['P', 'S'].includes(getAt(risk, 'building.residenceUsage')) &&
          (originalImportedTerm ||
            isGenuinelyBelowCoverageCMinimum({
              coverageA: getAt(risk, 'coverages.coverageA'),
              coverageC: getAt(risk, 'coverages.coverageC'),
              minPctOfCoverageA: coverageCMinPctOfCoverageA(risk, state)
            }))
      }
    },
    identity: ['type', 'building.buildingType', 'building.occupancy', 'building.buildingClass']
  },
  // farmLocations[].risks[FO6Scheduled].coverages.coverageF[] (#37243, Forreston): nothing is exempted; the marker only
  // carries the load-time defaults the item form fills in (LOAD_DEFAULT_EQUIVALENTS), which depend on the category.
  scheduledFarmProperty: {
    fields: {},
    identity: ['category']
  },
  // farmLocations[].risks[].building.swimmingPool: GO records only that a pool exists. The questions
  // depend on the pool type (identity), and only apply to a pool that arrived as one.
  swimmingPool: {
    fields: {
      hasRemovableStepsOrLockingGate: { activeWhen: (pool) => ['A', 'I'].includes(pool.typeOfPool) },
      has4FtHighFenceWithSelfLatchingGate: { activeWhen: (pool) => ['A', 'I'].includes(pool.typeOfPool) },
      hasDivingBoardOrPoolSlide: { activeWhen: (pool) => ['A', 'I'].includes(pool.typeOfPool) }
    },
    identity: ['typeOfPool']
  },
  // farmLocations[].risks[].coverages.scheduleCoverages[].items[]: every rule keys on the item's
  // category (and a boat's classification), both identity; the schedule's coverage type can't change.
  scheduledItem: {
    fields: {
      name: {},
      description: {},
      'extendedDetails.name': {},
      'extendedDetails.description': {},
      'extendedDetails.year': {},
      'extendedDetails.serialNumber': {},
      'extendedDetails.make': {},
      'extendedDetails.model': {},
      'extendedDetails.cc': {},
      'extendedDetails.classification': {},
      'extendedDetails.horsepower': {},
      'extendedDetails.lengthFt': {},
      'extendedDetails.milesPerHour': {},
      'extendedDetails.weight': {},
      'extendedDetails.dimensions': {},
      'extendedDetails.deductibleAllOther': {},
      'extendedDetails.deductibleWindHail': {}
    },
    identity: ['category', 'extendedDetails.classification']
  },
  // farmLiabilityCoverage.additionalAssetsExtensions[]: rules key on type (identity) and are skipped
  // for an asset tied to a location, risk or existing asset, so detaching it re-validates.
  liabilityAsset: {
    fields: Object.fromEntries(
      [
        'name',
        'description',
        'classification',
        'year',
        'make',
        'model',
        'serialNumber',
        'cc',
        'horsepower',
        'weight',
        'length',
        'mph'
      ].map((field) => [field, { controllers: ['locationId', 'riskId', 'existingAssetId'] }])
    ),
    identity: ['type', 'coverage']
  },
  // farmLiabilityCoverage.additionalPropertiesExtensions[]: same shape as assets.
  liabilityProperty: {
    fields: Object.fromEntries(
      [
        'description',
        'numberOfAcres',
        'numFamilies',
        'addressDetails.address',
        'addressDetails.city',
        'addressDetails.state',
        'addressDetails.zip'
      ].map((field) => [field, { controllers: ['locationId', 'riskId'] }])
    ),
    identity: ['type', 'coverage']
  },
  // farmInterestedParties[]: required for every relationship (identity).
  interestedParty: {
    fields: {
      entityType: {},
      address: {},
      'address.address': {},
      'address.city': {},
      'address.state': {},
      'address.zip': {}
    },
    identity: ['relationship']
  },
  // cars[] (auto): rental limit is always required; the PO-box rule reads the address only.
  car: {
    fields: {
      // #37133: controller: Comprehensive, which CFM's transportation expense requires
      limitRental: { controllers: ['deductibleComprehensive'] },
      'garageLocation.address': {},
      // #37133: lease/loan GO wrote on a car the state's EmberTech rules don't offer it on (MO non-BIX).
      // The car form turned it off on load, dropping it from the record and the dec. It stays until the
      // user turns it off; a deductible change brings back the rule that it needs both deductibles.
      coverageLoan: {
        controllers: ['deductibleCollision', 'deductibleComprehensive'],
        activeWhen: (car) => car?.coverageLoan === true
      }
    },
    identity: ['VIN']
  },
  // trailers[] (auto)
  trailer: {
    fields: { 'garageLocation.address': {} },
    identity: ['VIN']
  },
  // autoCoverage (auto, one per policy): UIM BI is checked against BI/PD and UM BI.
  autoCoverage: {
    fields: { policyLimitUIMBI: { controllers: ['policyLimitBIPD', 'policyLimitUMBI'] } },
    identity: []
  }
};

const getAt = (obj, path) => path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

/**
 * #37133: values the Details form's business-rule effects fill in on load that mean the same as the blank
 * a migrated item arrived with. GO never wrote them, so every endorsement of an unchanged imported item
 * listed them as changes ("phantom" itemsChanged) without changing what is covered or rated. The form keeps
 * showing the filled-in value, so the screen and its validation are unchanged; `restoreMigratedLoadDefaults`
 * puts the arrived blank back in the payload, while the item and every `controllers` field still hold what
 * they arrived with.
 *
 * Each entry: `equivalent(current, item)` says whether the filled-in value means "as arrived".
 */
const LOAD_DEFAULT_EQUIVALENTS = {
  risk: {
    // FO-1/2/3, mobile home and barndominium: hidden fields the dwelling forms default when empty. GO carries
    // neither, and `typeOfHome` is a 360Value input, so sending the default re-valued the dwelling.
    'building.typeOfHome': { controllers: ['building.propertyType'], equivalent: (current) => current === '1' },
    'building.ownershipStatus': { controllers: ['building.propertyType'], equivalent: (current) => current === 'P' },
    // FO-6: the three surfacing flags follow the umbrella cosmetic damage exclusion, which GO did write.
    ...Object.fromEntries(
      ['exteriorDoorsWindowsDamageExclusion', 'exteriorWallsDamageExclusion', 'roofsDamageExclusion'].map((field) => [
        `coverages.${field}`,
        {
          controllers: ['coverages.cosmeticDamageExclusion', 'coverages.cosmeticDamageUwOverride'],
          equivalent: (current, item) => current === Boolean(getAt(item, 'coverages.cosmeticDamageExclusion'))
        }
      ])
    ),
    // FO-6 Type 1/2 barn, confinement or poultry on RC: the RC provision is part of the RC settlement.
    'coverages.hasReplacementCostCoverage': {
      controllers: ['coverages.structureLossSettlement'],
      equivalent: (current, item) => current === true && getAt(item, 'coverages.structureLossSettlement') === 'RC'
    },
    // Mobile / modular home: "replacement cost terms" follow the loss settlement; GO wrote it only when on.
    'coverages.replCostMobileHome': {
      controllers: ['coverages.lossSettlement', 'building.propertyType'],
      equivalent: (current) => current === false
    }
  },
  // #37243 (Forreston): what scheduled-farm-property-item.jsx fills in on an IMT item that has none. `state` is the
  // item's location state, for the Coverage F config the form reads.
  scheduledFarmProperty: {
    // The rule's wind/hail for the item's limit and All Peril, as the form computes it.
    deductibleWindHail: {
      controllers: ['limit', 'deductible'],
      equivalent: (current, item, { state }) => {
        const covFConfig = scheduledFarmPropertyConfig(state);
        return (
          Boolean(covFConfig?.showWindHailDeductible) &&
          current ===
            getWindHailOptions({
              coverage: item.limit,
              allPerilDeductible: item.deductible,
              floor: covFConfig?.windHailDeductibleFloor
            }).targetValue
        );
      }
    },
    // Only where the category's override makes them always on; anywhere else a ticked box is the user's.
    hasOpenPerils: {
      controllers: [],
      equivalent: (current, item, { state }) =>
        current === true &&
        Boolean(scheduledFarmPropertyConfig(state)?.itemOverrides?.[item.category]?.openPerilsAlwaysOn)
    },
    hasWinterPerils: {
      controllers: [],
      equivalent: (current, item, { state }) =>
        current === true &&
        Boolean(scheduledFarmPropertyConfig(state)?.itemOverrides?.[item.category]?.winterPerilsAlwaysOn)
    }
  },
  car: {
    // CFM transportation expense: with no Comprehensive, or on a stated-amount vehicle (#37129), "No coverage" is
    // the only option; otherwise the $30 tier is the included one, which is also what a blank rates as (the rater
    // sends 30 unless the limit is 60+).
    limitRental: {
      controllers: ['deductibleComprehensive', 'statedAmount'],
      equivalent: (current, car) => {
        const offered =
          Boolean(car?.deductibleComprehensive) &&
          car.deductibleComprehensive !== 'NONE' &&
          !(Number(car?.statedAmount) > 0);
        return current === (offered ? '30' : 'NONE');
      }
    }
  }
};

// The Coverage F config the Scheduled farm property item form reads for a location state.
const scheduledFarmPropertyConfig = (state) => appConfig?.farm?.[state]?.risks?.[FarmRiskType.Fo6Scheduled]?.coverageF;

// The value at `path` as it arrived, telling "absent" apart from null so it can be restored exactly.
const arrivedAt = (obj, path) => {
  const keys = path.split('.');
  const parent = keys.length === 1 ? obj : getAt(obj, keys.slice(0, -1).join('.'));
  const last = keys[keys.length - 1];
  return parent != null && Object.hasOwn(parent, last) ? { absent: false, value: parent[last] } : { absent: true };
};

/** The dwelling's Coverage C minimum as a fraction of Coverage A (FO-2 / FO-3 P/S), or undefined. */
const coverageCMinPctOfCoverageA = (risk, state) =>
  resolveFarmCoverageCRules({
    residenceUsage: getAt(risk, 'building.residenceUsage'),
    lossSettlement: getAt(risk, 'coverages.lossSettlement'),
    businessRulesC: appConfig?.farm?.[state]?.risks?.[risk?.type]?.businessRules?.coverageC
  })?.minPctOfCoverageA;

// '' / null / undefined all mean "blank": the form turns a cleared field into '' and GraphQL into null.
const normalize = (value) => (value === '' || value === undefined ? null : value);

const snapshot = (item, paths) => Object.fromEntries(paths.map((path) => [path, normalize(getAt(item, path))]));

/**
 * #37233: the fields whose change makes policy-mod re-pull a migrated risk's 360Value whatever the brand's re-run
 * rules: the UW's "Refresh 360Value" (farm-segment.manager re-runs when it goes from off to on) and the Structure
 * Category (a different category is a different valuation). The seven re-run fields (isMigrated360RerunChange) are
 * not listed: they don't re-run on Forreston. Forreston's scope only (see markItem).
 */
const VALUATION_RERUN_TRIGGERS = ['coverages.uwRefresh360Value', 'building.propertyType'];

const markItem = (
  item,
  ruleName,
  { originalImportedTerm = false, state = null, onlyFields, exceptFields, withLoadDefaults = false } = {}
) => {
  if (!item || typeof item !== 'object') {
    return item;
  }
  const { fields, identity } = CARRY_OVER_RULES[ruleName];
  // Only fields whose controlling coverage was on when the item arrived (or that have none).
  // `onlyFields` / `exceptFields` narrow a brand that opts into just part of the carry-over.
  const eligibleFields = Object.keys(fields).filter(
    (path) =>
      (!onlyFields || onlyFields.includes(path)) &&
      !exceptFields?.includes(path) &&
      (!fields[path].activeWhen || fields[path].activeWhen(item, { originalImportedTerm, state }))
  );
  const controllers = [...new Set(eligibleFields.flatMap((path) => fields[path].controllers ?? []))];
  // `withLoadDefaults` is a partial scope opting one rule into the restore (#37243), not the CFM full carry-over.
  const full = !onlyFields && !exceptFields && !withLoadDefaults;
  // #37133: the blanks the item arrived with that a load-time default may fill in (full carry-over, or a rule a partial
  // scope opts into with `withLoadDefaults`).
  const loadDefaults =
    !full && !withLoadDefaults
      ? {}
      : Object.fromEntries(
          Object.entries(LOAD_DEFAULT_EQUIVALENTS[ruleName] ?? {})
            .filter(([path]) => normalize(getAt(item, path)) === null)
            .map(([path, { controllers: loadControllers }]) => [
              path,
              { arrived: arrivedAt(item, path), controllers: snapshot(item, loadControllers) }
            ])
        );
  return {
    ...item,
    [MIGRATED_CARRY_OVER_MARKER]: {
      rule: ruleName,
      fields: snapshot(item, eligibleFields),
      controllers: snapshot(item, controllers),
      identity: snapshot(item, identity),
      // #37233: what a 360Value re-run reads its trigger from (see rerunsValuation). Forreston's scope only: CFM's full
      // carry-over keeps, through a refresh, the limits its post-data rules grandfather (Coverage C, the GO roof
      // settlement), and the roof-only scope's roof settlement is kept server-side the same way.
      ...(ruleName === 'risk' && exceptFields && { valuation: snapshot(item, VALUATION_RERUN_TRIGGERS) }),
      // #37133: whether the item carries the full carry-over (CFM), not a part of it (Forreston).
      full,
      loadDefaults
    }
  };
};

/**
 * #37141: each dwelling's Coverage A as the term was written — its first segment, which the importer (GO's limit)
 * or the renewal (that limit plus the inflation guard) wrote. The Coverage A validation keeps a migrated dwelling
 * from being lowered below it (yup-helpers), as the farm post-data rules do.
 */
const writtenDwellingCoverageAByRiskId = (policy) => {
  const byRiskId = {};
  for (const location of policy?.segments?.[0]?.farmLocations || []) {
    for (const risk of location?.risks || []) {
      const coverageA = risk?.coverages?.coverageA;
      if (risk?.id && typeof coverageA === 'number' && Number.isFinite(coverageA)) {
        byRiskId[risk.id] = coverageA;
      }
    }
  }
  return byRiskId;
};

const withWrittenCoverageA = (markedRisk, writtenCoverageA) => {
  const coverageA = markedRisk?.id ? writtenCoverageA[markedRisk.id] : undefined;
  return coverageA === undefined || !markedRisk?.[MIGRATED_CARRY_OVER_MARKER]
    ? markedRisk
    : {
        ...markedRisk,
        [MIGRATED_CARRY_OVER_MARKER]: { ...markedRisk[MIGRATED_CARRY_OVER_MARKER], writtenCoverageA: coverageA }
      };
};

const markList = (list, ruleName, options) =>
  Array.isArray(list) ? list.map((item) => markItem(item, ruleName, options)) : list;

const markRisk = (risk, options) => {
  const markedRisk = markItem(risk, 'risk', options);
  if (markedRisk?.building?.swimmingPool) {
    markedRisk.building = {
      ...markedRisk.building,
      swimmingPool: markItem(markedRisk.building.swimmingPool, 'swimmingPool', options)
    };
  }
  const scheduleCoverages = risk?.coverages?.scheduleCoverages;
  if (!Array.isArray(scheduleCoverages)) {
    return markedRisk;
  }
  return {
    ...markedRisk,
    coverages: {
      ...markedRisk.coverages,
      scheduleCoverages: scheduleCoverages.map((scheduleCoverage) =>
        scheduleCoverage
          ? { ...scheduleCoverage, items: markList(scheduleCoverage.items, 'scheduledItem', options) }
          : scheduleCoverage
      )
    }
  };
};

/**
 * True when the policy came from a legacy-system migration: the imported term itself or any renewal
 * in its lineage. The renewal creator drops `source` on the first renewal and stamps
 * `wasRenewedFromImportedTerm` instead, so both are checked. The account's other terms are read too,
 * because the FE shows the imported in-force term while its renewal may be the one being edited.
 */
export const isMigratedPolicyLineage = (policy, policies = []) =>
  isImportedPolicy(policy) || wasRenewedFromImportedLineage(policy, policies);

const isEnabledForBrand = () => Boolean(appConfig?.featureFlags?.relaxMigratedCarryOverValidation);
const isForrestonRequiredFieldsBrand = () => Boolean(appConfig?.featureFlags?.relaxMigratedRequiredFields);

/** Every carry-over in CARRY_OVER_RULES (`relaxMigratedCarryOverValidation`, CFM). */
export const FULL_CARRY_OVER = 'full';
/**
 * Only an FO-6 structure's roof loss settlement (`keepMigratedRoofSettlement`, Forreston, #37115): the
 * FO-6 form leaves it as it is and its validation error is exempted while it is unchanged, as the farm
 * post-data rules keep it. Every other field and item keeps full validation.
 */
export const ROOF_SETTLEMENT_CARRY_OVER = 'roofSettlement';
const ROOF_SETTLEMENT_ONLY = ['coverages.roofLossSettlement'];
/**
 * Every field exemption but the coverage-limit ones (`relaxMigratedRequiredFields`, Forreston, #37233): an item
 * that came over keeps the fields IMT never carried (a dwelling's roof shape, a structure's height, an SPP item's
 * make) while they are as they arrived. Coverage C below the percent-of-A minimum is not kept: CFM's post-data rules
 * keep it server-side (importedTermCoverageCFloor), Forreston's don't; one at or above the unrounded minimum is
 * (keepsMigratedCoverageC, #37244). Nor is the full carry-over's locked Coverage D (marker `full`), and load-time
 * defaults are restored only on FO-6 Scheduled Coverage F items (#37243), not on risks or cars.
 */
export const REQUIRED_FIELDS_CARRY_OVER = 'requiredFields';
const COVERAGE_LIMIT_CARRY_OVERS = ['coverages.coverageC'];

const carryOverScopeForBrand = () => {
  if (isEnabledForBrand()) {
    return FULL_CARRY_OVER;
  }
  if (appConfig?.featureFlags?.relaxMigratedRequiredFields) {
    return REQUIRED_FIELDS_CARRY_OVER;
  }
  if (appConfig?.featureFlags?.keepMigratedRoofSettlement) {
    return ROOF_SETTLEMENT_CARRY_OVER;
  }
  return null;
};

const scopeForEnabled = (enabled) => (enabled ? FULL_CARRY_OVER : null);

const MIGRATED_DWELLING_TYPES = [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3];

/**
 * Forreston's narrow carry-over: an FO-6 structure's roof settlement (#37115). A dwelling that came over is marked
 * too, with no field exempted (#37141): the marker only tells the 360Value re-run confirmation and the Coverage A
 * maximum floor, both behind `confirmMigrated360Rerun`, that the dwelling arrived in the migration.
 */
/**
 * #37217: the ids of the risks a term started with (its first segment), or null when the term carries no stored
 * segments to tell from (every existing risk then counts as carried). Mirrors policy-mod's riskIdsTermStartedWith: a
 * risk an endorsement added after the migration was valued like new business.
 */
const riskIdsTermStartedWith = (policy) => {
  const riskIds = (policy?.segments?.[0]?.farmLocations || [])
    .flatMap((location) => (location?.risks || []).map((risk) => risk?.id))
    .filter(Boolean);
  return riskIds.length > 0 ? new Set(riskIds) : null;
};

// #37217: only a structure the term started with keeps an RC settlement without an RCE (migratedRcWithoutRce).
const withCarriedFromTermStart = (markedStructure, termStartRiskIds) => ({
  ...markedStructure,
  [MIGRATED_CARRY_OVER_MARKER]: {
    ...markedStructure[MIGRATED_CARRY_OVER_MARKER],
    carriedFromTermStart: !termStartRiskIds || termStartRiskIds.has(markedStructure.id)
  }
});

const markRoofSettlementOnly = (risk, termStartRiskIds) => {
  if (risk?.type === FarmRiskType.Fo6Structures) {
    return withCarriedFromTermStart(markItem(risk, 'risk', { onlyFields: ROOF_SETTLEMENT_ONLY }), termStartRiskIds);
  }
  return MIGRATED_DWELLING_TYPES.includes(risk?.type) ? markItem(risk, 'risk', { onlyFields: [] }) : risk;
};

/**
 * Whether the FO-2 / FO-3 form's Coverage C clamp leaves a migrated Coverage C under the percent-of-A
 * minimum as it is, mirroring the Details-tab exemption above: on the imported term any such Coverage C
 * (#37093), on a migrated renewal only a genuinely lower ratio (#37109). Only while Coverage A, Coverage C
 * and the rule's other inputs (loss settlement, residence usage) are the values the form loaded with, as
 * the backend floor (`importedTermCoverageCFloor`) and the Details-tab `controllers` require.
 *
 * @param {object} args
 * @param {object|null} args.policy the term the policy route is servicing (null off the policy route)
 * @param {number|null} args.minPctOfCoverageA the dwelling's percent-of-A rule, or null for a flat band
 */
export const keepsMigratedCoverageC = ({
  policy,
  coverageA,
  coverageC,
  lossSettlement,
  residenceUsage,
  initialCoverageA,
  initialCoverageC,
  initialLossSettlement,
  initialResidenceUsage,
  minPctOfCoverageA
}) => {
  const keepsAtUnroundedMinimum = isForrestonRequiredFieldsBrand();
  if (
    !(isEnabledForBrand() || keepsAtUnroundedMinimum) ||
    minPctOfCoverageA == null ||
    Number(coverageA) !== Number(initialCoverageA) ||
    coverageC !== initialCoverageC ||
    normalize(lossSettlement) !== normalize(initialLossSettlement) ||
    normalize(residenceUsage) !== normalize(initialResidenceUsage)
  ) {
    return false;
  }
  // #37244 (Forreston): its post-data rules floor Coverage C at the unrounded A x pct, so a migrated Coverage C at or
  // above it (an exact 75% with cents) stays as stored instead of being rounded up to the whole-dollar minimum, which
  // changed Coverage C on every endorsement (and failed Clarion Door's covC_limit_change factor in Forreston dev).
  if (keepsAtUnroundedMinimum && !isEnabledForBrand()) {
    return (
      (isImportedPolicy(policy) || Boolean(policy?.wasRenewedFromImportedTerm)) &&
      Number(coverageC) >= Number(coverageA) * minPctOfCoverageA
    );
  }
  if (isOriginalImportedTerm(policy)) {
    return true;
  }
  return (
    Boolean(policy?.wasRenewedFromImportedTerm) &&
    isGenuinelyBelowCoverageCMinimum({ coverageA, coverageC, minPctOfCoverageA })
  );
};

/**
 * #37133: whether the FO-2 / FO-3 form leaves a migrated dwelling's locked Coverage D as it arrived. GO
 * rounded it, so recomputing it from Coverage A on load moved it by a dollar and listed it as a change. Only
 * while Coverage A and the rule's inputs (residence usage, loss settlement) are the values the form loaded
 * with; any change to them re-derives it as before. Off the full carry-over (no marker) this is always false.
 */
export const keepsMigratedLockedCoverageD = ({
  item,
  coverageA,
  initialCoverageA,
  residenceUsage,
  initialResidenceUsage,
  lossSettlement,
  initialLossSettlement
}) =>
  Boolean(item?.[MIGRATED_CARRY_OVER_MARKER]?.full) &&
  Number(coverageA) === Number(initialCoverageA) &&
  normalize(residenceUsage) === normalize(initialResidenceUsage) &&
  normalize(lossSettlement) === normalize(initialLossSettlement);

/**
 * Stamp every existing item in a migrated policy's Details form values with the values it arrived
 * with. Returns `values` untouched when the brand hasn't opted in or the policy isn't migrated.
 * `scope` defaults to the brand's opt-in; `enabled: true` / `false` forces the full carry-over / none.
 */
export const hydrateMigratedCarryOverForForm = (
  values,
  {
    policy,
    policies,
    enabled,
    scope = enabled === undefined ? carryOverScopeForBrand() : scopeForEnabled(enabled)
  } = {}
) => {
  if (!scope || !values || !isMigratedPolicyLineage(policy, policies)) {
    return values;
  }
  if (scope === ROOF_SETTLEMENT_CARRY_OVER) {
    const termStartRiskIds = riskIdsTermStartedWith(policy);
    return Array.isArray(values.farmLocations)
      ? {
          ...values,
          farmLocations: values.farmLocations.map((location) =>
            location
              ? {
                  ...location,
                  risks: (location.risks || []).map((risk) => markRoofSettlementOnly(risk, termStartRiskIds))
                }
              : location
          )
        }
      : values;
  }
  const { farmLiabilityCoverage } = values;
  const originalImportedTerm = isOriginalImportedTerm(policy);
  const requiredFieldsOnly = scope === REQUIRED_FIELDS_CARRY_OVER;
  // The coverage-limit carry-overs, and the written Coverage A floor that mirrors CFM's post-data rules, are CFM's.
  const writtenCoverageA = requiredFieldsOnly ? {} : writtenDwellingCoverageAByRiskId(policy);
  // null (no stored segments) counts every existing structure as carried, deliberately: a bound term always has segments.
  const termStartRiskIds = riskIdsTermStartedWith(policy);
  const itemOptions = requiredFieldsOnly ? { exceptFields: COVERAGE_LIMIT_CARRY_OVERS } : {};
  const markForScope = (risk, riskOptions) => {
    const marked = withWrittenCoverageA(markRisk(risk, { ...riskOptions, ...itemOptions }), writtenCoverageA);
    // #37217 / #37629: every scope marks FO-6 structures for the RC-without-RCE exemption (migratedRcWithoutRce).
    if (risk?.type === FarmRiskType.Fo6Structures) {
      return withCarriedFromTermStart(marked, termStartRiskIds);
    }
    // #37243: Forreston's Scheduled farm property items carry their load-time defaults (CFM's are left as they were).
    if (requiredFieldsOnly && risk?.type === FarmRiskType.Fo6Scheduled && Array.isArray(marked.coverages?.coverageF)) {
      return {
        ...marked,
        coverages: {
          ...marked.coverages,
          coverageF: markList(marked.coverages.coverageF, 'scheduledFarmProperty', { withLoadDefaults: true })
        }
      };
    }
    return marked;
  };
  return {
    ...values,
    ...(Array.isArray(values.farmLocations) && {
      farmLocations: values.farmLocations.map((location) => {
        if (!location) {
          return location;
        }
        const riskOptions = { originalImportedTerm, state: location.address?.state };
        return {
          ...location,
          risks: (location.risks || []).map((risk) => markForScope(risk, riskOptions))
        };
      })
    }),
    ...(farmLiabilityCoverage && {
      farmLiabilityCoverage: {
        ...farmLiabilityCoverage,
        additionalAssetsExtensions: markList(
          farmLiabilityCoverage.additionalAssetsExtensions,
          'liabilityAsset',
          itemOptions
        ),
        additionalPropertiesExtensions: markList(
          farmLiabilityCoverage.additionalPropertiesExtensions,
          'liabilityProperty',
          itemOptions
        )
      }
    }),
    ...(values.farmInterestedParties && {
      farmInterestedParties: markList(values.farmInterestedParties, 'interestedParty', itemOptions)
    }),
    ...(values.cars && { cars: markList(values.cars, 'car', itemOptions) }),
    ...(values.trailers && { trailers: markList(values.trailers, 'trailer', itemOptions) }),
    ...(values.autoCoverage && { autoCoverage: markItem(values.autoCoverage, 'autoCoverage', itemOptions) })
  };
};

/**
 * True when `error` is on an allowlisted field that a migrated item still holds exactly as it
 * arrived. Judged by the NEAREST marked ancestor of the error's path, so a field inside a scheduled
 * item is judged by the item, not by the risk that contains it.
 */
export const isMigratedCarryOverError = (error, values) => {
  if (!error?.path || !values) {
    return false;
  }
  const segments = toPath(error.path);
  for (let ownerLength = segments.length - 1; ownerLength > 0; ownerLength -= 1) {
    const owner = getAt(values, segments.slice(0, ownerLength).join('.'));
    if (owner?.[MIGRATED_CARRY_OVER_MARKER]) {
      return !rerunsValuation(owner) && holdsMigratedCarryOverValue(owner, segments.slice(ownerLength).join('.'));
    }
  }
  return false;
};

/**
 * #37233: whether a migrated risk's 360Value is re-run by this endorsement (see VALUATION_RERUN_TRIGGERS), so the
 * fields it came over without are required again, as for a risk valued new. Only the risk's own fields: the items in it
 * (pool, scheduled items) aren't 360Value inputs. A refresh already on when the term loaded was an earlier
 * endorsement's and re-runs nothing now.
 */
const rerunsValuation = (risk) => {
  const arrived = risk?.[MIGRATED_CARRY_OVER_MARKER]?.valuation;
  if (!arrived) {
    return false;
  }
  const refreshTurnedOn =
    Boolean(getAt(risk, 'coverages.uwRefresh360Value')) && !arrived['coverages.uwRefresh360Value'];
  // Any category diff, a blank one filled in included: policy-mod re-runs on it (getAgriculturalStructureDiff /
  // getFarmDwellingDiff flag propertyType null → 'AG' as needsRecalc, and farm-segment.manager keeps that re-run
  // under Forreston's refresh-only rule).
  const categoryChanged = !isEqual(normalize(getAt(risk, 'building.propertyType')), arrived['building.propertyType']);
  return refreshTurnedOn || categoryChanged;
};

/**
 * True when `item` is a migrated item that still holds the allowlisted field at `fieldPath` exactly as
 * it arrived, as the same item (identity) and under the same selections (controllers). The form's
 * business-rule effects use it to leave such a value alone (#37110).
 */
export const holdsMigratedCarryOverValue = (item, fieldPath) => {
  const marker = item?.[MIGRATED_CARRY_OVER_MARKER];
  if (!marker || !Object.hasOwn(marker.fields, fieldPath)) {
    return false;
  }
  const unchanged = (path, arrivedValue) => isEqual(normalize(getAt(item, path)), arrivedValue);
  const sameItem = Object.entries(marker.identity).every(([path, arrivedValue]) => unchanged(path, arrivedValue));
  const sameSelection = (CARRY_OVER_RULES[marker.rule]?.fields[fieldPath]?.controllers ?? []).every((path) =>
    unchanged(path, marker.controllers[path])
  );
  return sameItem && sameSelection && unchanged(fieldPath, marker.fields[fieldPath]);
};

/**
 * True when a migrated FO-6 structure still holds the roof settlement it arrived with, under the same
 * category, class, occupancy, year built, structure settlement and roof restriction. The FO-6 form then
 * leaves the roof settlement and roof restriction alone, as the farm post-data rules do (#37110). A
 * structure that arrived with no roof settlement is defaulted like any other. Truthy, not `!= null`: the
 * form turns a cleared select into ''.
 */
export const keepsMigratedRoofSettlement = (structure) =>
  Boolean(structure?.coverages?.roofLossSettlement) &&
  !restrictionSwitchedOff(structure.coverages) &&
  holdsMigratedCarryOverValue(structure, 'coverages.roofLossSettlement');

// The user switching "Roof exclusion/restriction" off while a restriction is still set is a change to the
// roof (the form then lifts the restriction). The switch isn't a carry-over controller: FO6 turns an
// undefined switch on at mount for imported data, so only an explicit `false` counts. Raised by devlo on #37112.
const restrictionSwitchedOff = (coverages) =>
  coverages.hasRoofExclusionRestriction === false &&
  Boolean(coverages.roofExclusionRestriction) &&
  String(coverages.roofExclusionRestriction).toLowerCase() !== 'none';

/** The yup `inner` errors that still apply once migrated carry-over fields are exempted. */
export const filterMigratedCarryOverErrors = (errors, values) =>
  (errors || []).filter((error) => !isMigratedCarryOverError(error, values));

/**
 * #37133: the item with each load-time default (LOAD_DEFAULT_EQUIVALENTS) put back to the blank it arrived
 * with, while the item is the same item (identity) under the same selections (controllers) and the filled-in
 * value means the same. Anything else, including a value the user chose, is left as it is.
 */
const restoreItemLoadDefaults = (item, context = {}) => {
  const marker = item?.[MIGRATED_CARRY_OVER_MARKER];
  const entries = Object.entries(marker?.loadDefaults ?? {});
  if (!entries.length) {
    return item;
  }
  const unchanged = (path, arrivedValue) => isEqual(normalize(getAt(item, path)), arrivedValue);
  if (!Object.entries(marker.identity).every(([path, arrivedValue]) => unchanged(path, arrivedValue))) {
    return item;
  }
  const restored = structuredClone(item);
  for (const [path, { arrived, controllers }] of entries) {
    const current = getAt(item, path);
    const { equivalent } = LOAD_DEFAULT_EQUIVALENTS[marker.rule][path];
    if (
      normalize(current) === null ||
      !Object.entries(controllers).every(([controller, arrivedValue]) => unchanged(controller, arrivedValue)) ||
      !equivalent(current, item, context)
    ) {
      continue;
    }
    const keys = path.split('.');
    const parent = keys.length === 1 ? restored : getAt(restored, keys.slice(0, -1).join('.'));
    if (arrived.absent) {
      delete parent[keys[keys.length - 1]];
    } else {
      parent[keys[keys.length - 1]] = arrived.value;
    }
  }
  return restored;
};

// A risk, and (#37243) its Scheduled farm property items, with their load-time defaults put back.
const restoreRiskLoadDefaults = (risk, context) => {
  const restored = restoreItemLoadDefaults(risk, context);
  const coverageF = restored?.coverages?.coverageF;
  return Array.isArray(coverageF)
    ? {
        ...restored,
        coverages: { ...restored.coverages, coverageF: coverageF.map((item) => restoreItemLoadDefaults(item, context)) }
      }
    : restored;
};

/**
 * #37133: the Details form values with every migrated item's load-time defaults put back to what the item
 * arrived with (see LOAD_DEFAULT_EQUIVALENTS), so an endorsement sends an untouched imported item as it is
 * stored. Call on submit, before building the segment payload. A no-op for items without a marker.
 */
export const restoreMigratedLoadDefaults = (values) => {
  if (!values) {
    return values;
  }
  return {
    ...values,
    ...(Array.isArray(values.farmLocations) && {
      farmLocations: values.farmLocations.map((location) =>
        location
          ? {
              ...location,
              risks: (location.risks || []).map((risk) =>
                restoreRiskLoadDefaults(risk, { state: location.address?.state })
              )
            }
          : location
      )
    }),
    ...(Array.isArray(values.cars) && { cars: values.cars.map((car) => restoreItemLoadDefaults(car)) })
  };
};

/**
 * #37133: the value a migrated item arrived with at `fieldPath`, while it is still the same item (identity),
 * or undefined. Unlike holdsMigratedCarryOverValue it doesn't require the field to still hold that value, so
 * a coverage the user unchecks can be checked again.
 */
export const getMigratedArrivedValue = (item, fieldPath) => {
  const marker = item?.[MIGRATED_CARRY_OVER_MARKER];
  if (!marker || !Object.hasOwn(marker.fields, fieldPath)) {
    return undefined;
  }
  const sameItem = Object.entries(marker.identity).every(([path, arrivedValue]) =>
    isEqual(normalize(getAt(item, path)), arrivedValue)
  );
  return sameItem ? marker.fields[fieldPath] : undefined;
};
